/**
 * The orders this device shipped recently, newest first — what the home screen's "Last shipped" bar
 * lists, so a label that jammed or tore can be printed again without hunting for the order.
 *
 * Generate Labels sends the operator straight back to the home screen when RxPrint prints the
 * carton's labels by itself (see @/lib/shipHandoff), so there is no Reprint screen in the flow any
 * more. This is the way back to one: each entry opens the order's Documents page, whose buttons
 * re-print (RxPrint) or download every document the order has.
 *
 * Kept per vendor company in localStorage, so it survives the webview being reloaded or killed
 * mid-shift. Only the order's id and its label are stored — nothing that changes after ship.
 */
import { createStore, useStore } from 'zustand';

export type RecentShipment = {
  /** The order whose Shipping page shipped it (a bundle's lead order). Its Documents page reprints. */
  orderId: string;
  /** "SO #00000117" or "Bundle B-0001". */
  label: string;
  /** How many orders went out under it: 1, or a bundle's member count. */
  orderCount: number;
  shippedAt: number;
  /** True when RxPrint printed its carton labels by itself (the operator went straight home). */
  printed: boolean;
};

/** Enough to cover "the last few cartons at this bench" without becoming an order history. */
export const RECENT_SHIPMENTS_LIMIT = 5;
/** A shift. Older entries are dropped. */
export const RECENT_SHIPMENT_TTL_MS = 12 * 60 * 60 * 1000;

const STORAGE_KEY = 'warehouse.recentShipments.v1';

type Persisted = Record<string, RecentShipment[]>;

const isEntry = (value: any): value is RecentShipment =>
  !!value &&
  typeof value.orderId === 'string' &&
  typeof value.label === 'string' &&
  typeof value.shippedAt === 'number';

const readPersisted = (): Persisted => {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).map(([companyId, list]) => [
        companyId,
        Array.isArray(list) ? list.filter(isEntry) : []
      ])
    );
  } catch {
    return {};
  }
};

const writePersisted = (value: Persisted) => {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private mode / quota: the bar still works for this session.
  }
};

export const recentShipmentsStore = createStore<{ byCompany: Persisted }>(() => ({
  byCompany: readPersisted()
}));

const fresh = (list: RecentShipment[] | undefined, now: number) =>
  (list || []).filter((entry) => now - entry.shippedAt < RECENT_SHIPMENT_TTL_MS);

/** Put a shipment at the top of the company's list (replacing an older entry for the same order). */
export function recordRecentShipment(
  companyId: string | null | undefined,
  entry: Omit<RecentShipment, 'shippedAt'> & { shippedAt?: number }
): void {
  if (!companyId || !entry?.orderId) return;
  const now = entry.shippedAt ?? Date.now();
  const next: RecentShipment = { ...entry, orderId: String(entry.orderId), shippedAt: now };
  recentShipmentsStore.setState((state) => {
    const list = [
      next,
      ...fresh(state.byCompany[companyId], now).filter((e) => e.orderId !== next.orderId)
    ].slice(0, RECENT_SHIPMENTS_LIMIT);
    const byCompany = { ...state.byCompany, [companyId]: list };
    writePersisted(byCompany);
    return { byCompany };
  });
}

export const recentShipmentsFor = (
  companyId: string | null | undefined,
  now = Date.now()
): RecentShipment[] =>
  companyId ? fresh(recentShipmentsStore.getState().byCompany[companyId], now) : [];

const EMPTY: RecentShipment[] = [];

/** The company's recent shipments, live. Expired entries are filtered out by the caller's clock. */
export const useRecentShipments = (companyId: string | null | undefined): RecentShipment[] =>
  useStore(recentShipmentsStore, (state) =>
    companyId ? state.byCompany[companyId] || EMPTY : EMPTY
  );

/** "just now", "4 min ago", "2 hr ago". */
export function shippedAgo(shippedAt: number, now = Date.now()): string {
  const minutes = Math.floor(Math.max(0, now - shippedAt) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} hr ago`;
}
